// スキルの段（試作・スズネ）：下のキャラから上へ「行」が積み上がる。主Lvで1行ずつ開き、各行から1つだけ選ぶ（解放力は使わない）。
// 1つ上の行で選べるのは、選んだマスの真上・斜め上のマスだけ（1マスの行と、その上下はどれでも）。開いた行は必ずどれか1つを選んでいる。
// 位置は横7列の半マス（0..6）。n マスの行は 4:[0,2,4,6] 3:[1,3,5] 2:[2,4] 1:[3]。隣り合う＝位置の差が2以内
import type { ModKey } from './mods';
import type { CharId } from '../types';

export interface LadderRow {
  lv: number;
  /** 左から。スキルID（能力のマスは ladderStatId で作った ID） */
  ids: string[];
}
/** 能力のマス（行の中の数値の丸）：skills.ts がスキルにする */
export interface LadderStat {
  id: string;
  charId: CharId;
  lv: number;
  key: ModKey;
  value: number;
}

/** 能力のマスの1つ分の値（行の主Lvで伸びる：× (1 + Lv/400)） */
const STAT_BASE: Partial<Record<ModKey, number>> = { hp: 0.03, atk: 0.02, def: 0.035, agi: 0.012, core: 0.035, buildHp: 0.06, buildPower: 0.04, taken: -0.015, boss: 0.04 };
export const ladderStatId = (c: CharId, lv: number, key: string) => `${c}_lv${lv}_${key}`;

type Cell = string; // 'kokoroe'＝キャラ専用（<キャラ>_kokoroe）、'c_mat1'＝共通、'+hp'＝能力、'!hp'＝大きな能力（×2）
const SPEC: Partial<Record<CharId, [number, Cell[]][]>> = {
  suzune: [
    [1, ['kokoroe', 'kanabojutsu', 'onihada']],
    [25, ['+hp', '+atk', '+def']],
    [50, ['c_mat1', 'c_neutral1', 'c_potion1']],
    [75, ['+buildHp', '+core', '+hp', '+def']],
    [100, ['zumen', 'kanabo', 'fudo', 'hayazame']],
    [125, ['+buildPower', '+atk', '+def']],
    [150, ['c_bmax1', 'c_terr1', 'c_guard1']],
    [175, ['+hp', '+atk']],
    [200, ['daiku', 'ofushin', 'dandori']],
    [225, ['+buildHp', '+core', '+def']],
    [250, ['c_bp1', 'c_boss1', 'c_respawn1']],
    [275, ['+buildPower', '+atk', '+def', '+hp']],
    [300, ['shirokuzushi', 'tegiwa']],
    [325, ['+buildPower', '+core', '+hp']],
    [350, ['c_mat3', 'c_core2', 'c_potion3']],
    [375, ['+hp', '+atk', '+def']],
    [400, ['ougi_toppu', 'ougi', 'ougi_shuzen']],
    [425, ['+buildHp', '+atk', '+taken']],
    [450, ['c_bmax2', 'c_terr2', 'c_guard2']],
    [475, ['+buildPower', '+core', '+def', '+hp']],
    [500, ['hayadate', 'toryo']],
    [525, ['+hp', '+atk', '+agi']],
    [550, ['c_mat4', 'c_boss2', 'c_agi1']],
    [575, ['+buildHp', '+core', '+def']],
    [600, ['ogui', 'kishin']],
    [625, ['+buildPower', '+atk', '+def', '+hp']],
    [650, ['c_bp2', 'c_boss3', 'c_guard3']],
    [675, ['+hp', '+atk', '+def']],
    [700, ['!buildPower', '!atk', '!hp']],
  ],
};

export const LADDER_STATS: LadderStat[] = [];
export const LADDERS: Partial<Record<CharId, LadderRow[]>> = {};
/** 段を使うキャラ（スズネは系統の試作に移ったので外す。配置は残しておく） */
const ACTIVE: CharId[] = [];
for (const [c, rows] of Object.entries(SPEC).filter(([c]) => ACTIVE.includes(c as CharId)) as [CharId, [number, Cell[]][]][]) {
  LADDERS[c] = rows.map(([lv, cells]) => ({
    lv,
    ids: cells.map((x) => {
      if (x[0] === '+' || x[0] === '!') {
        const key = x.slice(1) as ModKey;
        const id = ladderStatId(c, lv, key);
        const v = (STAT_BASE[key] ?? 0.02) * (1 + lv / 400) * (x[0] === '!' ? 2 : 1);
        LADDER_STATS.push({ id, charId: c, lv, key, value: Math.round(v * 1000) / 1000 });
        return id;
      }
      return x.startsWith('c_') ? x : `${c}_${x}`;
    }),
  }));
}

export const skillLadder = (c: CharId | null | undefined): LadderRow[] | undefined => (c ? LADDERS[c] : undefined);
/** 横の位置（半マス 0..6） */
export const ladderX = (n: number, i: number) => (n === 1 ? 3 : n === 2 ? 2 + i * 2 : n === 3 ? 1 + i * 2 : i * 2);
/** 下の行の i マスと上の行の j マスがつながるか（1マスの行はどれとも） */
export const ladderLinked = (nA: number, i: number, nB: number, j: number) => nA === 1 || nB === 1 || Math.abs(ladderX(nA, i) - ladderX(nB, j)) <= 2;

const idxCache = new Map<CharId, Map<string, { row: number; i: number }>>();
/** スキルID → 行・行の中の位置 */
export function ladderAt(c: CharId, id: string): { row: number; i: number } | undefined {
  const L = LADDERS[c];
  if (!L) return undefined;
  let m = idxCache.get(c);
  if (!m) {
    m = new Map();
    L.forEach((r, row) => r.ids.forEach((x, i) => m!.set(x, { row, i })));
    idxCache.set(c, m);
  }
  return m.get(id);
}
/** その主Lvで開いている行の数 */
export const ladderOpen = (c: CharId, level: number) => (LADDERS[c] ?? []).filter((r) => r.lv <= level).length;
